import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
    Grid,
    Typography,
    Box,
    CircularProgress,
    TextField,
    IconButton,
    Button,
    InputAdornment
} from '@mui/material';
import { DragIndicator as DragIndicatorIcon } from '@mui/icons-material';
import TeamIosDarkToggle from 'team/shared/components/button/TeamIosDarkToggle';
import { useCardContext } from 'contexts/CardContext';
import { useParams, useLocation } from 'react-router-dom';
import { updateLeadCaptureMode, getCardById, updateCard } from 'apis/cardApi';
import TeamNotificationSnackbar from 'team/shared/components/notification/TeamNotificationSnackbar';
import { useAuth } from 'contexts/AuthContext';
import { AInputIcon } from 'icons/uiIcons';
import { CloseIcon, AddIcon } from 'icons/materialIcons';
import { Add as AddIconMUI } from '@mui/icons-material';
import { SubdirectoryArrowRight as SubdirectoryArrowRightIcon, ArrowDropDownRounded as ArrowDropDownRoundedIcon } from '@mui/icons-material';
import TeamWhiteButtonWithIcon from 'team/shared/components/button/TeamWhiteButtonWithIcon';
import TeamAddFieldModal from './TeamAddFieldModal';
import { normalizeField, normalizeFields, getFieldOptions, setFieldOptions } from './fieldNormalization';

const TeamLeadCaptureContainer = () => {
    const { cardState, setLeadCaptureModeOn, updateCardData } = useCardContext();
    const { cardId: paramCardId } = useParams();
    const { authState } = useAuth();
    const userId = authState.user?.userId;
    const location = useLocation();

    // ✅ TEMPLATE CONTEXT: Use authenticated user's card if no cardId from params
    // Check if we're in template edit page by checking the URL path
    const isTemplateContext = !paramCardId || location.pathname.includes('/template/');
    const userCardId = authState.user?.activeCardId ||
        (authState.user?.contactCardIds?.length > 0 ? authState.user.contactCardIds[0] : null);
    const cardId = isTemplateContext ? userCardId : paramCardId;

    // EXPLICIT STATE DECLARATIONS
    const [isInitialized, setIsInitialized] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [cardFullName, setCardFullName] = useState('');
    const [formHeader, setFormHeader] = useState('');
    const [isSavingFormHeader, setIsSavingFormHeader] = useState(false);
    const [inputFields, setInputFields] = useState([
        { label: 'Full Name', required: false, type: 'text' },
        { label: 'Email', required: true, type: 'text' },
        { label: 'Phone Number', required: false, type: 'text' },
        { label: 'Job Title', required: false, type: 'text' },
        { label: 'Company', required: false, type: 'text' },
        { label: 'Note', required: false, type: 'text' }
    ]);
    const [hasChanges, setHasChanges] = useState(false);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [adjacentSwapIndex, setAdjacentSwapIndex] = useState(null); // Index of adjacent field that should move
    const [swapOffset, setSwapOffset] = useState(0); // Offset for adjacent field animation
    const [isAddFieldModalOpen, setIsAddFieldModalOpen] = useState(false);
    const addFieldButtonRef = useRef(null);
    const fieldRefs = useRef({}); // Refs to track field positions
    const initialFormHeaderRef = useRef('');
    const initialInputFieldsRef = useRef([]);
    const [expandedDropdowns, setExpandedDropdowns] = useState(new Set()); // Track which dropdown fields are expanded
    const scrollContainerRef = useRef(null); // Reference to the scrollable container
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    // Always provide a boolean value to prevent controlled/uncontrolled warnings
    const leadCaptureModeOn = cardState[cardId]?.leadCaptureModeOn ?? false;

    // Fetch card data and initialize settings
    useEffect(() => {
        const fetchCardData = async () => {
            if (!cardId || isInitialized) return;

            try {
                setIsLoading(true);

                // ✅ FIX: Check if this is a new card creation (UUID) or template context
                const isNewCardCreation = cardId.includes('-') && cardId.length >= 32 && cardId.length <= 36;

                if (isNewCardCreation || isTemplateContext) {
                    // ✅ NEW CARD / TEMPLATE: Use default value (false) for lead capture mode, or get from server if template context
                    if (isTemplateContext) {
                        // For template context, try to get the actual setting from server
                        try {
                            const response = await getCardById(cardId);
                            if (response && response.card) {
                                const cardSetting = response.card.leadCaptureModeOn;
                                console.log('🔍 LeadCapture: Template context - Server value:', cardSetting);
                                setLeadCaptureModeOn(cardId, Boolean(cardSetting));

                                // Get card full name and form header
                                const fullName = response.card.contact_info?.fullName || '';
                                setCardFullName(fullName);

                                // Get form header from card, or use default
                                const savedFormHeader = response.card.leadCaptureFormHeader || '';
                                const defaultFormHeader = savedFormHeader || `Share your info back with ${fullName}`;
                                setFormHeader(defaultFormHeader);
                                initialFormHeaderRef.current = defaultFormHeader;

                                // Get input fields from card, or use default
                                const savedInputFields = response.card.leadCaptureInputFields || [];
                                const defaultInputFields = savedInputFields.length > 0 ? normalizeFields(savedInputFields) : [
                                    { label: 'Full Name', required: false, type: 'text' },
                                    { label: 'Email', required: true, type: 'text' },
                                    { label: 'Phone Number', required: false, type: 'text' },
                                    { label: 'Job Title', required: false, type: 'text' },
                                    { label: 'Company', required: false, type: 'text' },
                                    { label: 'Note', required: false, type: 'text' }
                                ];
                                setInputFields(defaultInputFields);
                                initialInputFieldsRef.current = JSON.parse(JSON.stringify(defaultInputFields));

                                setHasChanges(false);
                            } else {
                                console.log('🔍 LeadCapture: Template context - using default value (false)');
                                setLeadCaptureModeOn(cardId, false);
                            }
                        } catch (error) {
                            console.log('🔍 LeadCapture: Template context - error fetching, using default value (false)');
                            setLeadCaptureModeOn(cardId, false);
                        }
                    } else {
                        // New card creation
                        console.log('🔍 LeadCapture: New card creation - using default value (false)');
                        setLeadCaptureModeOn(cardId, false);

                        // Set default values for new card
                        const defaultFormHeader = cardFullName ? `Share your info back with ${cardFullName}` : 'Share your info back with...';
                        setFormHeader(defaultFormHeader);
                        initialFormHeaderRef.current = defaultFormHeader;

                        const defaultInputFields = [
                            { label: 'Full Name', required: false, type: 'text' },
                            { label: 'Email', required: true, type: 'text' },
                            { label: 'Phone Number', required: false, type: 'text' },
                            { label: 'Job Title', required: false, type: 'text' },
                            { label: 'Company', required: false, type: 'text' },
                            { label: 'Note', required: false, type: 'text' }
                        ];
                        setInputFields(defaultInputFields);
                        initialInputFieldsRef.current = JSON.parse(JSON.stringify(defaultInputFields));
                        setHasChanges(false);
                    }
                } else {
                    // ✅ EXISTING CARD: Get card-specific setting from server
                    const response = await getCardById(cardId);

                    if (response && response.card) {
                        const cardSetting = response.card.leadCaptureModeOn;
                        console.log('🔍 LeadCapture: Server value:', cardSetting);

                        // Update CardContext with server value
                        setLeadCaptureModeOn(cardId, Boolean(cardSetting));

                        // Get card full name and form header
                        const fullName = response.card.contact_info?.fullName || '';
                        setCardFullName(fullName);

                        // Get form header from card, or use default
                        const savedFormHeader = response.card.leadCaptureFormHeader || '';
                        const defaultFormHeader = savedFormHeader || `Share your info back with ${fullName}`;
                        setFormHeader(defaultFormHeader);
                        initialFormHeaderRef.current = defaultFormHeader;

                        // Get input fields from card, or use default
                        const savedInputFields = response.card.leadCaptureInputFields || [];
                        const defaultInputFields = savedInputFields.length > 0 ? normalizeFields(savedInputFields) : [
                            { label: 'Full Name', required: false, type: 'text' },
                            { label: 'Email', required: true, type: 'text' },
                            { label: 'Phone Number', required: false, type: 'text' },
                            { label: 'Job Title', required: false, type: 'text' },
                            { label: 'Company', required: false, type: 'text' },
                            { label: 'Note', required: false, type: 'text' }
                        ];
                        setInputFields(defaultInputFields);
                        initialInputFieldsRef.current = JSON.parse(JSON.stringify(defaultInputFields));

                        setHasChanges(false);
                    }
                }

                setIsInitialized(true);
            } catch (error) {
                console.error('Failed to load lead capture settings:', error);
                // ✅ FALLBACK: Use default value on error
                if (cardId) {
                    setLeadCaptureModeOn(cardId, false);
                }
            } finally {
                setIsLoading(false);
            }
        };

        fetchCardData();
    }, [cardId, userId, isInitialized, setLeadCaptureModeOn, isTemplateContext]);

    // Handle toggle change
    const handleToggle = (event) => {
        if (isLoading || !cardId) return;

        const isChecked = event.target.checked;
        console.log("Toggle switched:", isChecked);

        // Update local state first for immediate feedback
        setLeadCaptureModeOn(cardId, isChecked);

        // Update on the server using dedicated API
        updateLeadCaptureMode(cardId, isChecked)
            .then(() => {
                console.log("API call successful");

                setSnackbar({
                    open: true,
                    message: isChecked ? 'Lead capture mode enabled' : 'Lead capture mode disabled',
                    severity: 'success'
                });
            })
            .catch(error => {
                console.error("API call failed:", error);
                // Revert on error
                setLeadCaptureModeOn(cardId, !isChecked);

                setSnackbar({
                    open: true,
                    message: 'Failed to update lead capture mode',
                    severity: 'error'
                });
            });
    };

    // Check for changes
    useEffect(() => {
        const formHeaderChanged = formHeader !== initialFormHeaderRef.current;
        const inputFieldsChanged = JSON.stringify(inputFields) !== JSON.stringify(initialInputFieldsRef.current);
        setHasChanges(formHeaderChanged || inputFieldsChanged);
    }, [formHeader, inputFields]);

    // Update card context in real-time for preview (similar to About page)
    useEffect(() => {
        if (!cardId || !isInitialized) return;

        // Always update if we have input fields (even if form header is empty)
        // This ensures preview reflects changes immediately
        if (inputFields.length > 0) {
            // Normalize fields before updating context
            const normalizedFields = normalizeFields(inputFields);
            const updateData = {
                leadCaptureFormHeader: formHeader,
                leadCaptureInputFields: normalizedFields
            };

            // Update card context immediately for real-time preview
            updateCardData(cardId, updateData, false).catch(error => {
                console.error('Error updating card context for preview:', error);
            });
        }
    }, [cardId, formHeader, inputFields, isInitialized, updateCardData]);

    // Handle form header change
    const handleFormHeaderChange = (event) => {
        const newValue = event.target.value;
        setFormHeader(newValue);
    };

    // Save form header when user finishes editing (on blur) - removed auto-save, now handled by Save button
    const handleFormHeaderBlur = () => {
        // Just update the state, don't save yet
        // Saving will be handled by the Save button
    };

    // Handle input field toggle change - removed auto-save, now handled by Save button
    const handleInputFieldToggle = (index) => {
        if (isLoading) return;

        const updatedFields = [...inputFields];
        updatedFields[index].required = !updatedFields[index].required;

        // Update local state immediately for responsive UI
        setInputFields(updatedFields);
    };

    // Handle input field label change - only updates local state, does NOT save
    const handleInputFieldLabelChange = (index, newLabel) => {
        const updatedFields = [...inputFields];
        updatedFields[index].label = newLabel;
        setInputFields(updatedFields);
        // NO save here - only Save button saves
    };

    // Handle input field label blur - only validates, does NOT save
    const handleInputFieldLabelBlur = (index) => {
        const field = inputFields[index];
        if (!field.label.trim()) {
            // If label is empty, revert to original from initial values
            const updatedFields = [...inputFields];
            updatedFields[index].label = initialInputFieldsRef.current[index]?.label || 'Field';
            setInputFields(updatedFields);
        }
        // NO save here - only Save button saves
    };

    // Handle remove input field - removes from list (does NOT save immediately)
    const handleRemoveInputField = (index) => {
        if (isLoading || inputFields.length <= 1) {
            // Don't allow removing if only one field remains
            setSnackbar({
                open: true,
                message: 'At least one field is required',
                severity: 'warning'
            });
            return;
        }

        const updatedFields = inputFields.filter((_, i) => i !== index);
        setInputFields(updatedFields);
        // NO save here - only Save button saves
    };

    // Handle add field modal
    const handleAddFieldClick = () => {
        setIsAddFieldModalOpen(true);
    };

    const handleAddFieldModalClose = () => {
        setIsAddFieldModalOpen(false);
    };

    // Handle adding a new option to a dropdown field
    const handleAddDropdownOption = (fieldIndex) => {
        const updatedFields = [...inputFields];
        const field = updatedFields[fieldIndex];
        const options = getFieldOptions(field);
        updatedFields[fieldIndex] = setFieldOptions(field, [...options, '']);
        setInputFields(updatedFields);
    };

    // Handle removing an option from a dropdown field
    const handleRemoveDropdownOption = (fieldIndex, optionIndex) => {
        const updatedFields = [...inputFields];
        const field = updatedFields[fieldIndex];
        const options = getFieldOptions(field);
        if (options.length > optionIndex) {
            const newOptions = [...options];
            newOptions.splice(optionIndex, 1);
            updatedFields[fieldIndex] = setFieldOptions(field, newOptions);
            setInputFields(updatedFields);
        }
    };

    // Handle updating an option value in a dropdown field
    const handleDropdownOptionChange = (fieldIndex, optionIndex, newValue) => {
        const updatedFields = [...inputFields];
        const field = updatedFields[fieldIndex];
        let options = getFieldOptions(field);
        if (options.length <= optionIndex) {
            options = [...options];
            while (options.length <= optionIndex) {
                options.push('');
            }
        } else {
            options = [...options];
        }
        options[optionIndex] = newValue;
        updatedFields[fieldIndex] = setFieldOptions(field, options);
        setInputFields(updatedFields);
    };

    // Handle dropdown toggle (expand/collapse) - FIXED VERSION
    const handleDropdownToggle = useCallback((index) => {
        const fieldElement = fieldRefs.current[index];

        setExpandedDropdowns(prev => {
            const newSet = new Set(prev);
            const isClosing = newSet.has(index);

            // When opening, ensure dropdown has at least one empty option
            if (!isClosing) {
                setInputFields(prevFields => {
                    const updatedFields = [...prevFields];
                    if (updatedFields[index] && updatedFields[index].type === 'dropdown') {
                        const field = updatedFields[index];
                        const options = getFieldOptions(field);
                        if (options.length === 0) {
                            updatedFields[index] = setFieldOptions(field, ['']);
                        }
                    }
                    return updatedFields;
                });
            }

            if (isClosing) {
                // When closing, we need to adjust scroll to keep the field in view
                if (fieldElement && scrollContainerRef.current) {
                    const fieldRect = fieldElement.getBoundingClientRect();
                    const containerRect = scrollContainerRef.current.getBoundingClientRect();

                    // Calculate the field's position relative to the container's visible area
                    const fieldTopRelativeToContainer = fieldRect.top - containerRect.top;

                    // Store current scroll position of the container
                    const currentScrollTop = scrollContainerRef.current.scrollTop;

                    // After state update, adjust scroll to keep field visible
                    requestAnimationFrame(() => {
                        if (scrollContainerRef.current && fieldRefs.current[index]) {
                            const newFieldRect = fieldRefs.current[index].getBoundingClientRect();
                            const newContainerRect = scrollContainerRef.current.getBoundingClientRect();

                            // If the field would be above the visible area after collapse, scroll up
                            if (newFieldRect.top < newContainerRect.top) {
                                const scrollAdjustment = newContainerRect.top - newFieldRect.top;
                                scrollContainerRef.current.scrollTop = Math.max(0, currentScrollTop - scrollAdjustment);
                            }
                            // If field is below visible area, scroll to show it
                            else if (newFieldRect.bottom > newContainerRect.bottom) {
                                const scrollAdjustment = newFieldRect.bottom - newContainerRect.bottom;
                                scrollContainerRef.current.scrollTop = currentScrollTop + scrollAdjustment;
                            }
                        }
                    });
                }
                newSet.delete(index);
            } else {
                newSet.add(index);
            }
            return newSet;
        });
    }, []);

    // Helper function to get consistent InputAdornment for field icons
    const getFieldIconAdornment = (fieldType, index, isExpanded) => {
        // Common styles for all InputAdornments to ensure consistent text alignment
        const commonAdornmentStyles = {
            position: 'start',
            sx: {
                marginRight: '0', // Increased spacing from text for all field types
                marginLeft: '-0.875rem', // Move left to reduce space from drag icon
                paddingLeft: '0', // Ensure consistent padding
                minWidth: 'auto', // Prevent width differences
            }
        };

        switch (fieldType) {
            case 'dropdown':
                return (
                    <InputAdornment {...commonAdornmentStyles}>
                        <Box
                            sx={{
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginLeft: '-0.5rem', // Move icon left within the adornment (adjusted for new adornment marginLeft)
                                width: '2rem', // Fixed width to prevent layout shift and maintain alignment
                                height: '2rem', // Fixed height for consistency
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDropdownToggle(index);
                            }}
                        >
                            <ArrowDropDownRoundedIcon
                                sx={{
                                    fontSize: '2rem',
                                    color: '#828282',
                                    transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                                    transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                }}
                            />
                        </Box>
                    </InputAdornment>
                );
            case 'text':
            default:
                return (
                    <InputAdornment {...commonAdornmentStyles}>
                        <AInputIcon />
                    </InputAdornment>
                );
            // Add other field types (checkbox, file) as needed
        }
    };

    // Handle field type selection from modal
    const handleSelectFieldType = (fieldType) => {
        // Create a new field based on the selected type
        const defaultLabels = {
            text: 'Text',
            dropdown: 'Dropdown',
            checkbox: 'Checkbox',
            file: 'File Upload'
        };

        // Initialize config based on field type
        let config = {};
        if (fieldType === 'dropdown' || fieldType === 'radio' || fieldType === 'multiselect') {
            config = { options: [''] }; // Initialize with one empty option
        } else if (fieldType === 'checkbox') {
            config = { defaultValue: false };
        } else if (fieldType === 'file') {
            config = { allowedTypes: [], maxSize: 5242880, maxFiles: 1 }; // 5MB default
        }

        const newField = {
            label: defaultLabels[fieldType] || 'New Field',
            required: false,
            type: fieldType || 'text',
            config: config // Use flexible config object instead of options
        };

        const newIndex = inputFields.length;
        setInputFields([...inputFields, newField]);

        // If it's a dropdown, expand it by default (open stage)
        if (fieldType === 'dropdown') {
            setExpandedDropdowns(prev => {
                const newSet = new Set(prev);
                newSet.add(newIndex);
                return newSet;
            });
        }

        setIsAddFieldModalOpen(false); // Close the add field modal
    };

    // Drag and drop handlers for reordering input fields
    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        setAdjacentSwapIndex(null);
        setSwapOffset(0);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/html', e.target);
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';

        if (draggedIndex === null || draggedIndex === index) {
            setAdjacentSwapIndex(null);
            setSwapOffset(0);
            return;
        }

        // Only handle adjacent fields (immediate neighbors)
        const isAdjacent = Math.abs(draggedIndex - index) === 1;
        if (!isAdjacent) {
            setAdjacentSwapIndex(null);
            setSwapOffset(0);
            return;
        }

        // Get the field element to calculate position
        const fieldElement = fieldRefs.current[index];
        if (!fieldElement) {
            setAdjacentSwapIndex(null);
            setSwapOffset(0);
            return;
        }

        const rect = fieldElement.getBoundingClientRect();
        const mouseY = e.clientY;
        const fieldHeight = rect.height;

        // Calculate how much the dragged field has moved over the adjacent field
        let overlapRatio = 0;
        if (draggedIndex < index) {
            // Dragging down - field below should move up
            const distanceFromTop = mouseY - rect.top;
            overlapRatio = Math.max(0, Math.min(1, distanceFromTop / fieldHeight));
        } else {
            // Dragging up - field above should move down
            const distanceFromBottom = rect.bottom - mouseY;
            overlapRatio = Math.max(0, Math.min(1, distanceFromBottom / fieldHeight));
        }

        // Threshold: only start moving when dragged field has covered 30% of adjacent field
        const threshold = 0.3;
        if (overlapRatio > threshold) {
            setAdjacentSwapIndex(index);
            // Calculate offset: move adjacent field proportionally
            const excessOverlap = overlapRatio - threshold;
            const maxOffset = fieldHeight;
            const offset = (excessOverlap / (1 - threshold)) * maxOffset;
            setSwapOffset(draggedIndex < index ? -offset : offset);
        } else {
            setAdjacentSwapIndex(null);
            setSwapOffset(0);
        }
    };

    const handleDragLeave = (e) => {
        // Only clear if leaving the field area completely
        const relatedTarget = e.relatedTarget;
        const currentTarget = e.currentTarget;
        if (!relatedTarget || !currentTarget.contains(relatedTarget)) {
            // Check if we're still over an adjacent field
            const index = parseInt(currentTarget.getAttribute('data-index'));
            if (adjacentSwapIndex !== index) {
                setAdjacentSwapIndex(null);
                setSwapOffset(0);
            }
        }
    };

    const performSwap = () => {
        if (draggedIndex !== null && adjacentSwapIndex !== null) {
            const newFields = [...inputFields];
            const draggedField = newFields[draggedIndex];

            // Swap the two fields
            newFields[draggedIndex] = newFields[adjacentSwapIndex];
            newFields[adjacentSwapIndex] = draggedField;

            setInputFields(newFields);
            setHasChanges(true);
        }
    };

    const handleDrop = (e, dropIndex) => {
        e.preventDefault();

        // Perform swap if we have an adjacent swap index
        if (draggedIndex !== null && adjacentSwapIndex !== null) {
            performSwap();
        }

        setDraggedIndex(null);
        setDragOverIndex(null);
        setAdjacentSwapIndex(null);
        setSwapOffset(0);
    };

    const handleDragEnd = () => {
        // Perform swap if we have an adjacent swap index (user dragged past threshold)
        if (draggedIndex !== null && adjacentSwapIndex !== null) {
            performSwap();
        }

        setDraggedIndex(null);
        setDragOverIndex(null);
        setAdjacentSwapIndex(null);
        setSwapOffset(0);
    };

    // Reset to default values
    const handleResetToDefault = () => {
        const defaultFormHeader = cardFullName ? `Share your info back with ${cardFullName}` : 'Share your info back with...';
        setFormHeader(defaultFormHeader);

        const defaultInputFields = [
            { label: 'Full Name', required: false },
            { label: 'Email', required: true },
            { label: 'Phone Number', required: false },
            { label: 'Job Title', required: false },
            { label: 'Company', required: false },
            { label: 'Note', required: false }
        ];
        setInputFields(defaultInputFields);
    };

    // Handle cancel - revert to initial values (loaded from server)
    const handleCancel = () => {
        // Revert form header to initial value
        setFormHeader(initialFormHeaderRef.current);

        // Revert input fields to initial values (deep copy to avoid reference issues)
        const revertedFields = JSON.parse(JSON.stringify(initialInputFieldsRef.current));
        setInputFields(revertedFields);

        // Reset hasChanges flag
        setHasChanges(false);
    };

    // Handle save - save all changes
    const handleSave = async () => {
        if (!cardId || isSaving || !hasChanges) return;

        try {
            setIsSaving(true);

            // If form header is empty, use default value
            const trimmedHeader = formHeader.trim();
            const headerToSave = trimmedHeader || (cardFullName ? `Share your info back with ${cardFullName}` : 'Share your info back with...');

            // Normalize fields before saving to ensure config structure
            const normalizedFields = normalizeFields(inputFields);

            // Debug: Log fields being saved
            if (process.env.NODE_ENV === 'development') {
                console.log('🔍 Saving fields:', normalizedFields.map(f => ({
                    label: f.label,
                    type: f.type,
                    config: f.config,
                    options: getFieldOptions(f)
                })));
            }

            // Save both form header and input fields
            await updateCard(cardId, {
                leadCaptureFormHeader: headerToSave,
                leadCaptureInputFields: normalizedFields
            });

            // Update initial refs to match saved values
            initialFormHeaderRef.current = headerToSave;
            initialInputFieldsRef.current = JSON.parse(JSON.stringify(inputFields));
            setFormHeader(headerToSave);
            setHasChanges(false);

            setSnackbar({
                open: true,
                message: 'Lead capture settings saved successfully',
                severity: 'success'
            });
        } catch (error) {
            console.error("Failed to save lead capture settings:", error);
            setSnackbar({
                open: true,
                message: error?.message || 'Failed to save lead capture settings',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    // Log lead capture mode only after initialization
    useEffect(() => {
        if (isInitialized) {
            console.log("leadCaptureModeOn:", leadCaptureModeOn);
        }
    }, [leadCaptureModeOn, isInitialized]);

    // ✅ CHECK: Hide lead capture form during new card creation (but not in template context)
    const isNewCardCreation = cardId && !isTemplateContext && cardId.includes('-') && cardId.length >= 32 && cardId.length <= 36;

    // ✅ HIDE: Don't render anything during new card creation (but allow in template context)
    if (isNewCardCreation) {
        return null;
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden',
                position: 'relative'
            }}
        >
            {/* Header - Fixed */}
            <Box
                sx={{
                    padding: 2,
                    paddingBottom: 0,
                    backgroundColor: 'white',
                    borderBottom: '1px solid #e1e1e1',
                    flexShrink: 0,
                    position: 'sticky',
                    top: 0,
                    zIndex: 10
                }}
            >
                <Typography
                    variant="h6"
                    component="h2"
                    sx={{
                        fontWeight: 'bold',
                        marginBottom: 2
                    }}
                >
                    Lead Capture
                </Typography>
            </Box>

            {/* Content - Scrollable */}
            <Box
                ref={scrollContainerRef}
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    backgroundColor: 'white'
                }}
            >
                <Box sx={{ padding: 2 }}>
                    <Grid container alignItems="center" spacing={2}>
                        <Grid item xs={12} sm={8}>
                            <Typography
                                variant="body1"
                                sx={{
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    color: "#828282"
                                }}
                            >
                                When lead capture mode is enabled, the lead form will pop up as soon as your profile is shared.
                            </Typography>
                        </Grid>

                        <Grid
                            item
                            xs={12}
                            sm={4}
                            display="flex"
                            justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
                        >
                            {isLoading ? (
                                <CircularProgress size={24} />
                            ) : (
                                <TeamIosDarkToggle
                                    checked={Boolean(leadCaptureModeOn)}
                                    onChange={handleToggle}
                                />
                            )}
                        </Grid>
                    </Grid>

                    {/* Form Header Section */}
                    <Box sx={{ marginTop: 4 }}>
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                color: "#4f4f4f",
                                marginBottom: "0.625rem"
                            }}
                        >
                            Form Header
                        </Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder={cardFullName ? `Share your info back with ${cardFullName}` : 'Share your info back with...'}
                            value={formHeader}
                            onChange={handleFormHeaderChange}
                            onBlur={handleFormHeaderBlur}
                            disabled={isSavingFormHeader || isLoading}
                            sx={{
                                backgroundColor: '#f7f7f7',
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        border: 'none',
                                    },
                                    '&:hover fieldset': {
                                        border: 'none',
                                    },
                                    '&.Mui-focused fieldset': {
                                        border: 'none',
                                    },
                                    '& input::placeholder': {
                                        fontSize: '0.8rem',
                                        fontWeight: '300',
                                        opacity: 0.6,
                                    },
                                    '& input': {
                                        fontSize: '0.8rem',
                                        fontWeight: '400',
                                    },
                                },
                            }}
                        />
                    </Box>

                    {/* Input Fields Section */}
                    <Box sx={{ marginTop: 4 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: "10px" }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    color: "#4f4f4f",
                                }}
                            >
                                Input Fields
                            </Typography>
                            <TeamWhiteButtonWithIcon
                                ref={addFieldButtonRef}
                                icon={AddIcon}
                                text="Add field"
                                onClick={handleAddFieldClick}
                                disabled={isLoading}
                                padding="0.3125rem 1rem"
                                sx={{
                                    border: '0.75px solid #dedede',
                                    borderRadius: '0.625rem',
                                    fontSize: '0.8rem',
                                    '&:hover': {
                                        backgroundColor: '#f7f7f7',
                                        border: '0.75px solid #dedede',
                                        boxShadow: 'none',
                                    },
                                    '&.Mui-disabled': {
                                        border: '0.75px solid #e0e0e0',
                                        color: '#a6a6a6'
                                    }
                                }}
                            />
                        </Box>
                        {inputFields.map((field, index) => {
                            const isDragging = draggedIndex === index;
                            const isAdjacentSwap = adjacentSwapIndex === index;
                            const isDropdown = field.type === 'dropdown';
                            const isExpanded = expandedDropdowns.has(index);

                            return (
                                <Box
                                    key={index}
                                    data-index={index}
                                    ref={(el) => {
                                        if (el) fieldRefs.current[index] = el;
                                    }}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, index)}
                                    onDragOver={(e) => handleDragOver(e, index)}
                                    onDragLeave={handleDragLeave}
                                    onDrop={(e) => handleDrop(e, index)}
                                    onDragEnd={handleDragEnd}
                                    sx={{
                                        backgroundColor: '#f7f7f7',
                                        borderRadius: '0.25rem',
                                        marginBottom: 1,
                                        cursor: isDragging ? 'grabbing' : 'grab',
                                        opacity: isDragging ? 0.3 : 1,
                                        transform: isAdjacentSwap ? `translateY(${swapOffset}px)` : 'translateY(0)',
                                        transition: isAdjacentSwap ? 'transform 0.15s ease-out' : 'opacity 0.2s ease',
                                        position: 'relative',
                                        zIndex: isDragging ? 10 : isAdjacentSwap ? 5 : 1,
                                    }}
                                >
                                    {/* Main Field Row */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            padding: '0.25rem 0.5rem',
                                            gap: '0.75rem', // Reduced gap to bring icons closer to drag icon
                                        }}
                                    >
                                        {/* Drag icon for all fields */}
                                        <Box
                                            sx={{
                                                cursor: 'grab',
                                                display: 'flex',
                                                alignItems: 'center',
                                                paddingTop: '0.25rem',
                                                marginRight: '-0.5rem', // Reduce gap to icons
                                                '&:active': {
                                                    cursor: 'grabbing'
                                                }
                                            }}
                                            onMouseDown={(e) => e.stopPropagation()}
                                        >
                                            <DragIndicatorIcon sx={{ fontSize: '1.125rem', color: '#828282' }} />
                                        </Box>

                                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                                            <TextField
                                                variant="outlined"
                                                fullWidth
                                                value={field.label}
                                                onChange={(e) => handleInputFieldLabelChange(index, e.target.value)}
                                                onBlur={() => handleInputFieldLabelBlur(index)}
                                                disabled={isLoading}
                                                InputProps={{
                                                    startAdornment: getFieldIconAdornment(field.type, index, isExpanded)
                                                }}
                                                sx={{
                                                    backgroundColor: '#f7f7f7',
                                                    '& .MuiOutlinedInput-root': {
                                                        backgroundColor: '#f7f7f7',
                                                        padding: '0.25rem 0.5rem',
                                                        '& fieldset': {
                                                            border: 'none',
                                                        },
                                                        '&:hover fieldset': {
                                                            border: 'none',
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            border: 'none',
                                                        },
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        fontSize: '0.8rem',
                                                        fontWeight: '400',
                                                        color: '#212121',
                                                        padding: '0.25rem 0',
                                                        paddingLeft: '0', // Ensure consistent left padding for all fields
                                                    },
                                                    '& .MuiInputAdornment-root': {
                                                        marginLeft: '0', // Remove default left margin
                                                        marginTop: '0', // Remove default top margin
                                                        marginBottom: '0', // Remove default bottom margin
                                                        // marginRight is controlled by commonAdornmentStyles
                                                    },
                                                }}
                                            />
                                        </Box>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                border: '1px solid #D9D9D9',
                                                borderRadius: '100px',
                                                padding: '0.125rem 0.375rem',
                                                gap: 1
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontSize: '0.625rem',
                                                    color: '#828282'
                                                }}
                                            >
                                                REQUIRE
                                            </Typography>
                                            <TeamIosDarkToggle
                                                checked={field.required}
                                                onChange={(event) => handleInputFieldToggle(index)}
                                            />
                                        </Box>

                                        <IconButton
                                            onClick={() => handleRemoveInputField(index)}
                                            disabled={isLoading || inputFields.length <= 1}
                                            sx={{ color: '#9E9E9E' }}
                                        >
                                            <CloseIcon />
                                        </IconButton>
                                    </Box>

                                    {/* Dropdown Option Input Rows - Only for dropdown fields when expanded */}
                                    {isDropdown && isExpanded && (() => {
                                        const options = getFieldOptions(field);
                                        const displayOptions = options.length > 0 ? options : [''];
                                        return (
                                            <Box>
                                                {/* Render existing options - ensure at least one option is shown */}
                                                {displayOptions.map((option, optionIndex) => (
                                                    <Box
                                                        key={optionIndex}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            padding: '0 0.5rem 0.25rem 0.5rem', // Remove top padding, keep bottom and sides
                                                            marginTop: optionIndex === 0 ? '0rem' : '0rem', // No margin for first, none for others
                                                            gap: 1,
                                                        }}
                                                    >
                                                        {/* Spacer to align arrow icon with dropdown icon (now inside InputAdornment) */}
                                                        <Box
                                                            sx={{
                                                                width: 'calc(1.125rem + 0.75rem - 0.875rem - 0.5rem + 1rem + 0.5625rem + 1rem)', // Position arrow center at dropdown center + additional offset: dropdown center (1.5rem) + half arrow width (0.5625rem) + extra offset (1rem)
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'flex-end', // Position arrow at the end to align center with dropdown
                                                            }}
                                                        >
                                                            {/* Arrow icon positioned to align with dropdown icon above (inside InputAdornment) */}
                                                            <SubdirectoryArrowRightIcon
                                                                sx={{
                                                                    fontSize: '1.125rem',
                                                                    color: '#828282',
                                                                }}
                                                            />
                                                        </Box>
                                                        <Box sx={{ flex: 1 }}>
                                                            <TextField
                                                                variant="outlined"
                                                                fullWidth
                                                                placeholder="Type An Option Here"
                                                                value={option}
                                                                onChange={(e) => handleDropdownOptionChange(index, optionIndex, e.target.value)}
                                                                disabled={isLoading}
                                                                InputProps={{
                                                                    startAdornment: null,
                                                                }}
                                                                sx={{
                                                                    backgroundColor: '#f7f7f7',
                                                                    '& .MuiOutlinedInput-root': {
                                                                        backgroundColor: '#f7f7f7',
                                                                        padding: '0.125rem 0.5rem', // Reduced top/bottom padding
                                                                        '& fieldset': {
                                                                            border: 'none',
                                                                        },
                                                                        '&:hover fieldset': {
                                                                            border: 'none',
                                                                        },
                                                                        '&.Mui-focused fieldset': {
                                                                            border: 'none',
                                                                        },
                                                                    },
                                                                    '& .MuiInputBase-input': {
                                                                        fontSize: '0.8rem',
                                                                        fontWeight: '400',
                                                                        color: '#212121',
                                                                        padding: '0.25rem 0',
                                                                    },
                                                                    '& .MuiInputBase-input::placeholder': {
                                                                        color: '#9E9E9E',
                                                                        opacity: 1,
                                                                    },
                                                                }}
                                                            />
                                                        </Box>

                                                        <IconButton
                                                            onClick={() => handleRemoveDropdownOption(index, optionIndex)}
                                                            disabled={isLoading}
                                                            sx={{ color: '#9E9E9E' }}
                                                        >
                                                            <CloseIcon />
                                                        </IconButton>
                                                    </Box>
                                                ))}

                                                {/* Add another option button */}
                                                <Box
                                                    onClick={() => handleAddDropdownOption(index)}
                                                    disabled={isLoading}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        padding: '0 0.5rem 0.25rem 0.5rem', // Same spacing as option rows
                                                        marginTop: '0rem', // Same spacing
                                                        gap: 1,
                                                        cursor: isLoading ? 'default' : 'pointer',
                                                        '&:hover': {
                                                            '& .add-option-text': {
                                                                color: isLoading ? '#828282' : '#212121', // Darker text on hover
                                                            },
                                                            '& .add-option-icon': {
                                                                color: isLoading ? '#828282' : '#212121', // Darker icon on hover
                                                            },
                                                        },
                                                    }}
                                                >
                                                    {/* Plus icon in place of arrow icon */}
                                                    <Box
                                                        sx={{
                                                            width: 'calc(1.125rem + 0.75rem - 0.875rem - 0.5rem + 1rem + 0.5625rem + 1rem)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'flex-end',
                                                        }}
                                                    >
                                                        <AddIconMUI
                                                            className="add-option-icon"
                                                            sx={{
                                                                fontSize: '1.125rem',
                                                                color: '#828282',
                                                                transition: 'color 0.2s',
                                                            }}
                                                        />
                                                    </Box>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography
                                                            className="add-option-text"
                                                            sx={{
                                                                fontSize: '0.8rem',
                                                                fontWeight: '400',
                                                                color: '#828282',
                                                                transition: 'color 0.2s',
                                                                padding: '0.125rem 0.5rem',
                                                                minHeight: '2.5rem',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                            }}
                                                        >
                                                            Add another option
                                                        </Typography>
                                                    </Box>
                                                    {/* Empty space to align with close icon column */}
                                                    <Box sx={{ width: '2.5rem' }} />
                                                </Box>
                                            </Box>
                                        );
                                    })()}
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </Box>

            {/* Footer with Reset, Cancel, and Save buttons - Fixed */}
            <Box
                sx={{
                    padding: 2,
                    borderTop: '1px solid #e1e1e1',
                    backgroundColor: 'white',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexShrink: 0,
                    position: 'sticky',
                    bottom: 0,
                    zIndex: 10
                }}
            >
                {/* Reset to default button */}
                <Button
                    variant="text"
                    onClick={handleResetToDefault}
                    disabled={isLoading || isSaving}
                    sx={{
                        color: '#000000',
                        textTransform: 'none',
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        padding: '0.375rem 0.75rem',
                        backgroundColor: 'transparent',
                        '&:hover': {
                            backgroundColor: '#f5f5f5',
                        },
                        '&.Mui-disabled': {
                            color: '#a6a6a6',
                        }
                    }}
                >
                    Reset to default
                </Button>

                {/* Cancel and Save buttons */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <TeamWhiteButtonWithIcon
                        text="Cancel"
                        onClick={handleCancel}
                        disabled={isSaving || !hasChanges || isLoading}
                        padding="5px 16px"
                        sx={{
                            border: '0.75px solid #dedede',
                            borderRadius: '10px',
                            fontSize: '0.8rem',
                            color: !hasChanges ? '#a6a6a6' : '#212121',
                            '&:hover': {
                                backgroundColor: !hasChanges ? '#ffffff' : '#f7f7f7',
                                border: '0.75px solid #dedede',
                                boxShadow: 'none',
                            },
                            '&.Mui-disabled': {
                                border: '0.75px solid #e0e0e0',
                                color: '#a6a6a6'
                            }
                        }}
                    />
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={isSaving || !hasChanges || isLoading}
                        sx={{
                            backgroundColor: !hasChanges ? '#e0e0e0' : '#000000',
                            padding: '0.3125rem 2rem',
                            color: !hasChanges ? '#a6a6a6' : '#ffffff',
                            fontWeight: "bold",
                            textTransform: 'none',
                            borderRadius: '10px',
                            fontSize: '0.8rem',
                            '&:hover': {
                                backgroundColor: !hasChanges ? '#e0e0e0' : '#333333',
                            },
                            '&.Mui-disabled': {
                                backgroundColor: '#e0e0e0',
                                color: '#a6a6a6'
                            }
                        }}
                    >
                        {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
                    </Button>
                </Box>
            </Box>

            {/* Snackbar */}
            <TeamNotificationSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                handleClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            />

            {/* Add Field Modal */}
            <TeamAddFieldModal
                open={isAddFieldModalOpen}
                onClose={handleAddFieldModalClose}
                onSelectFieldType={handleSelectFieldType}
                anchorEl={addFieldButtonRef.current}
            />
        </Box>
    );
};

export default TeamLeadCaptureContainer;